/**
 * Calendar-day arithmetic for the time screens (money plan phase 1). A day here is the YYYY-MM-DD the database sent:
 * a calendar day, never an instant (CLAUDE.md), so nothing goes through a time zone. The UTC calendar is used only to
 * count days between two such strings, which is exact. Weeks run Monday to Sunday (Q6).
 */

import { parseDateOnly } from '../dates.ts';

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

function toNumber(day: string): number {
  if (!CALENDAR_DATE.test(day)) throw new Error('not a calendar day');
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d) / 86_400_000;
}
function fromNumber(n: number): string {
  return new Date(n * 86_400_000).toISOString().slice(0, 10);
}

export function addDays(day: string, n: number): string {
  return fromNumber(toNumber(day) + n);
}

/** The Monday on or before a day. */
export function mondayOf(day: string): string {
  const weekday = new Date(toNumber(day) * 86_400_000).getUTCDay(); // 0 is Sunday
  return addDays(day, -((weekday + 6) % 7));
}

/** The seven days of the week that starts on a Monday. */
export function weekDays(monday: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

/** A day's short weekday name in the viewer's locale, read from the calendar day itself (dates.ts), never an instant. */
export function weekdayShort(day: string): string {
  return parseDateOnly(day).toLocaleDateString(undefined, { weekday: 'short' });
}

/** "YYYY-MM" as a month's name and year ("October 2026"), from the string alone. */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const names = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October',
    'November', 'December'];
  return `${names[m - 1] ?? month} ${y}`;
}
